import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, ListChecks, Loader2, Image as ImageIcon, TrendingUp, Trophy, Network } from "lucide-react";
import { MapQuiz } from "@/components/MapQuiz";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { getStudent } from "@/lib/student";
import { StudentGate } from "@/components/StudentGate";
import { Flashcards } from "@/components/Flashcards";
import { MultipleChoiceQuiz } from "@/components/MultipleChoiceQuiz";

export default function Quiz() {
  const [terms, setTerms] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState("");
  const [mode, setMode] = useState("flash");
  const [student, setStudentState] = useState(getStudent);

  useEffect(() => {
    api.get("/terms").then((r) => setTerms(r.data));
    api.get("/subjects").then((r) => setSubjects(r.data));
  }, []);

  if (!terms) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const pool = terms.filter((t) => !subject || t.subject === subject);
  const comicPool = pool.filter((t) => t.comic_status === "ready");
  const mapPool = pool.filter((t) => t.map_status === "ready" && t.concept_map?.branches?.length >= 2);
  const shuffle = () => setTerms((ts) => [...ts].sort(() => Math.random() - 0.5));
  const tab = (m) => `ink-box-sm flex items-center gap-2 rounded-full px-5 py-2 font-extrabold ${mode === m ? "bg-[var(--ink)] text-white" : "bg-white"}`;
  const finish = (variant) => async (score, total) => {
    if (!student) return toast.info("Inserisci nome e classe per salvare i punti la prossima volta");
    try {
      const { data } = await api.post("/quiz-results", { student_name: student.name, class_id: student.class_id, mode: variant, subject, score, total });
      toast.success("Punteggio salvato!");
      data.new_badges?.forEach((b) => toast.success(`Nuovo badge: ${b.title}!`, { description: b.description, duration: 6000 }));
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  const notEnough = mode === "flash" ? pool.length < 1 : mode === "map" ? mapPool.length < 1 : pool.length < 4 || (mode === "comic" && comicPool.length < 1);

  return (
    <main className="mx-auto max-w-5xl px-5 pb-24 pt-10 sm:px-8" data-testid="quiz-page">
      <h1 className="font-display text-4xl font-bold sm:text-5xl">Allenati!</h1>
      <p className="mt-2 text-base text-neutral-700">Ripassa con le flashcard o mettiti alla prova con il quiz prima della verifica.</p>
      <div className="mt-6"><StudentGate student={student} onChange={setStudentState} /></div>
      <div className="mt-3 flex flex-wrap gap-4 text-sm font-extrabold">
        <Link to="/progressi" data-testid="quiz-progress-link" className="flex items-center gap-1 hover:underline"><TrendingUp className="h-4 w-4" /> I miei progressi</Link>
        <Link to="/classifica" data-testid="quiz-leaderboard-link" className="flex items-center gap-1 hover:underline"><Trophy className="h-4 w-4" /> Classifica</Link>
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button onClick={() => setMode("flash")} data-testid="quiz-mode-flashcards" className={tab("flash")}><Layers className="h-4 w-4" /> Flashcard</button>
        <button onClick={() => setMode("quiz")} data-testid="quiz-mode-quiz" className={tab("quiz")}><ListChecks className="h-4 w-4" /> Quiz</button>
        <button onClick={() => setMode("comic")} data-testid="quiz-mode-comic" className={tab("comic")}><ImageIcon className="h-4 w-4" /> Quiz fumetti</button>
        <button onClick={() => setMode("map")} data-testid="quiz-mode-map" className={tab("map")}><Network className="h-4 w-4" /> Gioco mappe</button>
        <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="quiz-subject-select" className="ink-box-sm rounded-full bg-white px-4 py-2 font-extrabold outline-none">
          <option value="">Tutte le materie</option>
          {subjects.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="mt-10">
        {notEnough ? (
          <p className="ink-box rounded-3xl bg-white p-10 text-center font-bold" data-testid="quiz-not-enough">
            {mode === "flash" ? "Nessuna parola in questa materia." : mode === "map" ? "Non ci sono ancora mappe concettuali per questa materia." : mode === "comic" && pool.length >= 4 ? "Non ci sono ancora fumetti per questa materia." : "Servono almeno 4 parole in questa materia per fare il quiz."}
          </p>
        ) : mode === "flash" ? (
          <Flashcards key={subject} terms={pool} onShuffle={shuffle} />
        ) : mode === "map" ? (
          <MapQuiz key={`m-${subject}`} terms={mapPool} onFinish={finish("mappe")} />
        ) : mode === "comic" ? (
          <MultipleChoiceQuiz key={`c-${subject}`} terms={comicPool} allTerms={pool} variant="fumetti" onFinish={finish("fumetti")} />
        ) : (
          <MultipleChoiceQuiz key={`q-${subject}`} terms={pool} onFinish={finish("definizioni")} />
        )}
      </div>
    </main>
  );
}
